import { useState, type FormEvent } from "react";
import { auth } from "./client";
import { localizeError } from "./localizeError";
import { useI18n } from "../i18n/I18nProvider";
import { usePreferences } from "../preferences/usePreferences";
import { ThemeToggle } from "../workspace/ThemeToggle";
import { logoForTheme } from "../theme/brand";
export function AuthPage({
  resetToken = null,
  initialMode = "login",
  onRecovered,
}: {
  resetToken?: string | null;
  initialMode?: "login" | "signup";
  onRecovered: () => void;
}) {
  const { t, locale } = useI18n();
  const { theme, save: savePreferences, busy: savingPreferences, error: preferencesError } = usePreferences();
  const recovery = Boolean(resetToken);
  const [mode, setMode] = useState<"login" | "signup" | "reset">(initialMode);
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(""),
    [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (recovery) {
        await auth.resetPassword(resetToken!, password);
        setNotice(t("密码已更新，请使用新密码登录。", "Your password has been updated. Sign in with your new password."));
        setPassword("");
        onRecovered();
      } else if (mode === "signup") {
        const result = await auth.signUp({ email, password, locale, theme });
        if (result.status === "registration_complete") {
          await auth.signInWithPassword({ email, password });
        } else {
          setNotice(t("验证邮件已发送。请打开邮箱中的链接，验证后即可登录使用。", "Check your email for a verification link, then sign in."));
        }
      } else if (mode === "reset") {
        await auth.resetPasswordForEmail(email);
        setNotice(t("如果该邮箱已注册，你将收到重置密码邮件。", "If this email is registered, you will receive a password reset email."));
      } else {
        await auth.signInWithPassword({ email, password });
      }
    } catch (e) {
      setError(localizeError(e, locale, t("暂时无法登录，请稍后重试", "Unable to sign in. Please try again.")));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="product-auth">
      <section className="auth-card">
        {!recovery && <a href="#home" className="auth-home-link">← {t("返回主页", "Back to home")}</a>}
        <img className="auth-logo" src={logoForTheme(theme)} alt="Lyncia" />
        <div className="auth-preferences">
          <label>
            <span className="sr-only">{t("语言", "Language")}</span>
            <select aria-label={t("语言", "Language")} value={locale} disabled={busy || savingPreferences}
              onChange={(event) => void savePreferences({ locale: event.target.value as "zh-CN" | "en" }).catch(() => {})}>
              <option value="zh-CN">简体中文</option>
              <option value="en">English</option>
            </select>
          </label>
          <ThemeToggle />
        </div>
        {preferencesError && <p role="alert" className="notice error">{preferencesError}</p>}
        <h2>
          {recovery
            ? t("设置新密码", "Set a new password")
            : mode === "signup"
              ? t("创建 Lyncia 账户", "Create a Lyncia account")
              : mode === "reset"
                ? t("找回密码", "Reset password")
                : t("登录 Lyncia", "Sign in to Lyncia")}
        </h2>
        <p className="muted">
          {recovery
            ? t("设置密码后请重新登录你的空间。", "After setting your password, sign in again.")
            : t("创建账户即可开始，你的对话只属于你。", "Create an account to get started. Your conversations are yours.")}
        </p>
          <form onSubmit={submit}>
            {!recovery && (
              <label>
                {t("邮箱", "Email")}
                <input
                  autoComplete="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </label>
            )}
            {(recovery || mode !== "reset") && (
              <label>
                {t("密码", "Password")}
                <input
                  type="password"
                  required
                  minLength={12}
                  maxLength={128}
                  autoComplete={
                    recovery || mode === "signup"
                      ? "new-password"
                      : "current-password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("12–128 位字符", "12–128 characters")}
                />
              </label>
            )}
            {error && (
              <p role="alert" className="notice error">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="notice">
                {notice}
              </p>
            )}
            <button className="primary wide" disabled={busy}>
              {busy
                ? t("请稍候…", "Please wait…")
                : recovery
                  ? t("保存新密码", "Save new password")
                  : mode === "signup"
                    ? t("创建账户并开始", "Create account and start")
                    : mode === "reset"
                      ? t("发送重置邮件", "Send reset email")
                      : t("进入我的空间", "Sign in")}
            </button>
          </form>
        {!recovery && (
          <div className="auth-links">
            <button
              onClick={() => {
                setMode(mode === "signup" ? "login" : "signup");
                setNotice("");
                setError("");
              }}
            >
              {mode === "signup" ? t("已有账户，去登录", "Already have an account? Sign in") : t("创建账户", "Create account")}
            </button>
            <button
              onClick={() => {
                setMode(mode === "reset" ? "login" : "reset");
                setNotice("");
                setError("");
              }}
            >
              {mode === "reset" ? t("返回登录", "Back to sign in") : t("忘记密码？", "Forgot password?")}
            </button>
          </div>
        )}
        <p className="auth-foot">{t("文字 · 语音 · 你的对话", "Text · Voice · Your conversations")}</p>
      </section>
    </main>
  );
}
